import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getNewEnrollmentsOptions,
	getNewEnrollmentsQueryKey,
	deleteParentMutation,
} from "@/lib/client/@tanstack/react-query.gen";
import type { NewEnrollmentParentDto } from "@/lib/client/types.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { User, Phone, MapPin, Eye, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterText,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/new-enrollments/")({
	component: NewEnrollmentsPage,
});

const subjectLabels: Record<string, string> = {
	MATHS: "Mathématiques",
	FRANCAIS: "Français",
	SCIENCES_PHYSIQUE: "Sciences Physiques",
	ANGLAIS: "Anglais",
	SES: "SES",
};

const timeSlotLabels: Record<string, string> = {
	SATURDAY_MORNING_1: "Sam 8h30-10h30",
	SATURDAY_MORNING_2: "Sam 10h30-12h30",
	SATURDAY_AFTERNOON_1: "Sam 13h30-15h30",
	SATURDAY_AFTERNOON_2: "Sam 15h30-17h30",
	WEDNESDAY_MORNING_1: "Mer 11h30-13h30",
};

function NewEnrollmentsPage() {
	const queryClient = useQueryClient();
	const { data: parents = [], isLoading } = useQuery(
		getNewEnrollmentsOptions(),
	);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const deleteMutation = useMutation({
		...deleteParentMutation(),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: getNewEnrollmentsQueryKey(),
			});
			toast.success("Parent supprimé avec succès");
			setDeletingId(null);
		},
		onError: () => {
			toast.error("Erreur lors de la suppression du parent");
			setDeletingId(null);
		},
	});

	const handleDelete = (id: string) => {
		deleteMutation.mutate({
			path: { id },
		});
	};

	const columns = useMemo<ColumnDef<NewEnrollmentParentDto>[]>(
		() => [
			{
				id: "parent",
				accessorFn: (row) => row.lastName + " " + row.firstName,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Parent" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<User className="h-4 w-4 text-gray-400" />
						<div>
							<p className="font-medium">
								{row.original.firstName} {row.original.lastName}
							</p>
							<p className="text-sm text-gray-600">{row.original.email}</p>
						</div>
					</div>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.firstName} ${row.original.lastName}`
					);
					const reverseName = normalizeString(
						`${row.original.lastName} ${row.original.firstName}`
					);
					const email = normalizeString(row.original.email || "");
					return (
						fullName.includes(searchValue) ||
						reverseName.includes(searchValue) ||
						email.includes(searchValue)
					);
				},
				sortDescFirst: false,
				meta: { label: "Parent" },
			},
			{
				id: "contact",
				accessorFn: (row) => `${row.phone || ""} ${row.address || ""}`,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Contact" />
				),
				cell: ({ row }) => (
					<div className="space-y-1">
						<div className="flex items-center gap-1 text-sm">
							<Phone className="h-3 w-3" />
							{row.original.phone}
						</div>
						<div className="flex items-center gap-1 text-sm text-gray-600">
							<MapPin className="h-3 w-3" />
							{row.original.address?.substring(0, 30)}
							{row.original.address && row.original.address.length > 30
								? "..."
								: ""}
						</div>
					</div>
				),
				enableSorting: false,
				meta: {
					label: "Contact",
					textValue: (row: NewEnrollmentParentDto) => `${row.phone || ""} - ${row.address || ""}`,
				},
			},
			{
				id: "students",
				accessorFn: (row) => row.students?.length || 0,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Étudiants" />
				),
				cell: ({ row }) => (
					<Badge variant="outline">
						{row.original.students?.length || 0} enfant(s)
					</Badge>
				),
				meta: {
					label: "Étudiants",
					textValue: (row: NewEnrollmentParentDto) => {
						const students = row.students || [];
						if (students.length === 0) return "0";
						return students.map((s: any) => `${s.firstName} ${s.lastName}`).join(", ");
					},
				},
			},
			{
				id: "subjects",
				accessorFn: (row) => {
					const allSubjects = new Set<string>();
					row.students?.forEach((student) => {
						student.subjects?.forEach((subject) => allSubjects.add(subject));
					});
					return Array.from(allSubjects).map((s) => subjectLabels[s] || s).join(", ");
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Matières" />
				),
				cell: ({ row }) => {
					const allSubjects = new Set<string>();
					row.original.students?.forEach((student) => {
						student.subjects?.forEach((subject) => allSubjects.add(subject));
					});
					return (
						<div className="flex flex-wrap gap-1">
							{Array.from(allSubjects).map((subject) => (
								<Badge key={subject} variant="secondary" className="text-xs">
									{subjectLabels[subject] || subject}
								</Badge>
							))}
						</div>
					);
				},
				enableSorting: false,
				meta: { label: "Matières" },
			},
			{
				id: "timeSlots",
				accessorFn: (row) => {
					const allTimeSlots = new Set<string>();
					row.students?.forEach((student) => {
						student.timeSlots?.forEach((slot) => allTimeSlots.add(slot));
					});
					return Array.from(allTimeSlots).map((s) => timeSlotLabels[s] || s).join(", ");
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Créneaux" />
				),
				cell: ({ row }) => {
					const allTimeSlots = new Set<string>();
					row.original.students?.forEach((student) => {
						student.timeSlots?.forEach((slot) => allTimeSlots.add(slot));
					});
					return (
						<div className="flex flex-wrap gap-1">
							{Array.from(allTimeSlots).map((slot) => (
								<Badge key={slot} variant="outline" className="text-xs">
									{timeSlotLabels[slot] || slot}
								</Badge>
							))}
						</div>
					);
				},
				enableSorting: false,
				meta: { label: "Créneaux" },
			},
			{
				id: "createdAt",
				accessorKey: "createdAt",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Inscrit le" />
				),
				cell: ({ row }) => {
					const date = row.original.createdAt
						? new Date(row.original.createdAt)
						: null;
					return date ? (
						<span className="text-sm">
							{date.toLocaleDateString("fr-FR", {
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							})}
						</span>
					) : (
						"-"
					);
				},
				meta: {
					label: "Inscrit le",
					textValue: (row: NewEnrollmentParentDto) =>
						row.createdAt ? new Date(row.createdAt).toLocaleDateString("fr-FR") : "-",
				},
			},
			{
				id: "actions",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Actions" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<Link
							to="/admin/new-enrollments/$id"
							params={{ id: row.original.id || "" }}
						>
							<Button size="sm" variant="outline">
								<Eye className="h-3 w-3 mr-1" />
								Voir détails
							</Button>
						</Link>
						<AlertDialog>
							<AlertDialogTrigger asChild>
								<Button
									size="sm"
									variant="destructive"
									disabled={deleteMutation.isPending && deletingId === row.original.id}
								>
									<Trash2 className="h-3 w-3 mr-1" />
									Supprimer
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
									<AlertDialogDescription>
										Êtes-vous sûr de vouloir supprimer {row.original.firstName}{" "}
										{row.original.lastName} ? Cette action est irréversible et supprimera
										également tous les étudiants associés.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>Annuler</AlertDialogCancel>
									<AlertDialogAction
										onClick={() => {
											setDeletingId(row.original.id || null);
											handleDelete(row.original.id || "");
										}}
									>
										Supprimer
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				),
				enableSorting: false,
				meta: { label: "Actions" },
			},
		],
		[deleteMutation.isPending, deletingId],
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">
						Chargement des nouveaux inscrits...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold">Nouveaux inscrits</h1>
				<p className="text-gray-600 mt-2">
					Parents ayant complété leur inscription mais pas encore inscrits à un
					cours
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Liste des nouveaux inscrits ({parents.length})</CardTitle>
					<CardDescription>
						Parents en attente d'affectation à des cours
					</CardDescription>
				</CardHeader>
				<CardContent>
					{parents.length === 0 ? (
						<div className="text-center py-12">
							<p className="text-gray-500">
								Aucun nouveau inscrit en attente
							</p>
						</div>
					) : (
						<DataTable
							columns={columns}
							data={parents || []}
							emptyMessage="Aucun nouveau inscrit"
							toolbar={(table) => (
								<DataTableToolbar table={table} showExportExcel exportFilename="nouveaux-inscrits">
									{table.getColumn("parent") && (
										<DataTableFilterText
											column={table.getColumn("parent")!}
											placeholder="Rechercher un parent..."
										/>
									)}
								</DataTableToolbar>
							)}
						/>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
